import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Camera,
  Calendar as CalendarIcon,
  Lock,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { useAuth } from "@/context/AuthContext";
import { PrimaryButton, TextField, ErrorBox } from "@/components/AuthKit";
import UserAvatar from "@/components/UserAvatar";

/**
 * EditProfile — 1:1 web port of iOS `settings/edit-profile.tsx`.
 *
 * Reachable at `/settings/edit-profile` from the Settings root PROFILE row.
 *
 * Fields (in order):
 *   1) Avatar picker (88px + brand-purple camera badge) → `POST /profile_photo`
 *   2) "Change profile photo" text button — opens the same picker
 *   3) Display name
 *   4) Date of birth (min age 13) with locked age-13 client-side gate
 *   5) Bio (280 char, live counter)
 *   6) Location
 *   7) Pronouns
 *   8) Website
 *   9) Private-account toggle (`Lock` icon; brand-purple when ON)
 *   10) "Save changes" rainbow pill → `PATCH /profiles/update_me`
 */

const BRAND = "#7638FA";
const MIN_AGE = 13;

function ageOf(dobStr) {
  if (!dobStr) return 0;
  const d = new Date(dobStr);
  if (Number.isNaN(d.getTime())) return 0;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a;
}
function fmtDob(input) {
  if (!input) return "";
  return input.slice(0, 10); // already yyyy-mm-dd
}

/* -----------------------------------------------------------------------
 * Row wrapper for a light-mode row card with a leading icon + input body.
 * ----------------------------------------------------------------------- */
function ToggleRow({ Icon, title, subtitle, checked, onChange, testId }) {
  return (
    <div
      className="flex items-center gap-3 rounded-[16px] bg-card border border-border p-4"
      data-testid={testId}
    >
      <span
        className="w-[38px] h-[38px] rounded-[12px] flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${BRAND}18` }}
      >
        <Icon size={18} style={{ color: BRAND }} strokeWidth={2} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-foreground text-[15px] font-semibold">{title}</div>
        <div className="text-muted-foreground text-[12.5px] mt-0.5">{subtitle}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-[46px] h-[26px] rounded-full transition-colors ${
          checked ? "" : "bg-secondary"
        }`}
        style={checked ? { backgroundColor: BRAND } : {}}
        data-testid={`${testId}-toggle`}
      >
        <span
          className="absolute top-[3px] left-[3px] w-5 h-5 rounded-full bg-white shadow transition-transform"
          style={{ transform: checked ? "translateX(20px)" : "translateX(0)" }}
        />
      </button>
    </div>
  );
}

export default function EditProfile() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const inputPhotoRef = useRef(null);

  const [form, setForm] = useState({
    display_name: "",
    bio: "",
    location: "",
    website: "",
    pronouns: "",
    account_privacy: "public",
    date_of_birth: "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(EP.meProfile);
        if (cancelled) return;
        const raw = unwrap(res.data);
        const p = raw?.profile || raw?.user || raw || {};
        const dob =
          p.date_of_birth ||
          raw?.date_of_birth ||
          (raw?.profile && raw.profile.date_of_birth) ||
          "";
        setForm({
          display_name: p.display_name || "",
          bio: p.bio || "",
          location: p.location || "",
          website: p.website || "",
          pronouns: p.pronouns || "",
          account_privacy: p.account_privacy || "public",
          date_of_birth: fmtDob(dob),
        });
      } catch (e) {
        if (!cancelled) {
          // Fall back to auth user
          setForm((prev) => ({
            ...prev,
            display_name: user?.display_name || "",
            bio: user?.bio || "",
            location: user?.location || "",
            website: user?.website || "",
            pronouns: user?.pronouns || "",
            account_privacy: user?.account_privacy || "public",
          }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const back = () => (window.history.length > 1 ? navigate(-1) : navigate("/settings"));

  const onPickPhoto = () => inputPhotoRef.current?.click();

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Please choose an image under 8MB.");
      return;
    }
    setUploadingPhoto(true);
    setAvatarPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("image", file, file.name);
      await api.post(EP.profilePhoto, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("Profile photo updated.");
    } catch (e2) {
      toast.error(apiError(e2));
      setAvatarPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const doSave = async () => {
    if (saving) return;
    setErr("");
    const age = ageOf(form.date_of_birth);
    if (form.date_of_birth && age < MIN_AGE) {
      return setErr(`You must be at least ${MIN_AGE} years old to use BrontoY.`);
    }
    setSaving(true);
    try {
      const payload = {
        display_name: form.display_name.trim(),
        bio: form.bio.trim(),
        location: form.location.trim(),
        website: form.website.trim(),
        pronouns: form.pronouns.trim(),
        account_privacy: form.account_privacy,
      };
      if (form.date_of_birth) payload.date_of_birth = form.date_of_birth;
      await api.patch(EP.updateMe, payload);
      await refresh();
      toast.success("Profile updated.");
      back();
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-[100dvh] flex items-center justify-center bg-background"
        data-testid="edit-profile-loading"
      >
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: BRAND }} />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] w-full bg-background text-foreground flex flex-col" data-testid="edit-profile-screen">
      {/* Header */}
      <div
        className="h-[52px] px-2 flex items-center border-b border-border shrink-0"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <button
          type="button"
          onClick={back}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary"
          aria-label="Back"
          data-testid="edit-profile-back"
        >
          <ChevronLeft size={25} strokeWidth={2.4} />
        </button>
        <h1
          className="flex-1 text-center text-foreground font-jakarta font-bold text-[17px] pr-10"
          style={{ letterSpacing: "-0.3px" }}
        >
          Edit Profile
        </h1>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 pb-16">
        <div className="mx-auto w-full max-w-[560px]">
          {/* Avatar + change button */}
          <div className="flex flex-col items-center mt-6">
            <button
              type="button"
              onClick={onPickPhoto}
              className="relative w-[88px] h-[88px] rounded-full overflow-hidden active:opacity-85 transition-opacity"
              data-testid="settings-change-photo"
            >
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt=""
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <UserAvatar user={user} size="2xl" />
              )}
              <span
                className="absolute bottom-0 right-0 w-6 h-6 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: BRAND,
                  borderWidth: 2,
                  borderColor: "hsl(var(--background))",
                }}
              >
                <Camera size={14} className="text-white" strokeWidth={2.2} />
              </span>
              {uploadingPhoto && (
                <span
                  className="absolute inset-0 bg-black/40 flex items-center justify-center"
                  data-testid="settings-photo-loading"
                >
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={onPickPhoto}
              className="mt-2.5 text-[13.5px] font-bold hover:opacity-80"
              style={{ color: BRAND }}
            >
              {uploadingPhoto ? "Uploading…" : "Change profile photo"}
            </button>
            <input
              ref={inputPhotoRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onPhotoChange}
              className="hidden"
            />
          </div>

          {/* Fields */}
          <div className="flex flex-col gap-4 mt-7">
            <TextField
              label="DISPLAY NAME"
              value={form.display_name}
              onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
              maxLength={60}
              placeholder="Your name"
              testId="settings-display-name"
            />

            <div className="flex flex-col gap-1.5">
              <label
                className="text-muted-foreground font-semibold"
                style={{ fontSize: 13, letterSpacing: "0.02em" }}
              >
                DATE OF BIRTH
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                  <CalendarIcon size={18} className="text-muted-foreground" />
                </span>
                <input
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, date_of_birth: e.target.value }))
                  }
                  className="w-full h-[52px] bg-secondary rounded-[14px] pl-11 pr-4 text-[16px] text-foreground border border-border focus:outline-none focus:border-foreground/25 transition-colors"
                  data-testid="settings-dob"
                />
              </div>
              {form.date_of_birth && ageOf(form.date_of_birth) < MIN_AGE && (
                <p className="text-destructive text-xs mt-0.5" data-testid="settings-dob-error">
                  You must be at least {MIN_AGE} years old to use BrontoY.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                className="text-muted-foreground font-semibold"
                style={{ fontSize: 13, letterSpacing: "0.02em" }}
              >
                BIO
              </label>
              <textarea
                value={form.bio}
                onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value.slice(0, 280) }))}
                rows={3}
                maxLength={280}
                placeholder="A short line about yourself"
                className="w-full min-h-[88px] bg-secondary rounded-[14px] px-4 py-3 text-[16px] text-foreground placeholder:text-muted-foreground border border-border focus:outline-none focus:border-foreground/25 transition-colors resize-none"
                data-testid="settings-bio"
              />
              <p className="text-xs text-muted-foreground self-end">{form.bio.length}/280</p>
            </div>

            <TextField
              label="LOCATION"
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              placeholder="City, Country"
              testId="settings-location"
            />

            <TextField
              label="PRONOUNS"
              value={form.pronouns}
              onChange={(e) => setForm((f) => ({ ...f, pronouns: e.target.value }))}
              placeholder="she/her"
              autoCapitalize="none"
              testId="settings-pronouns"
            />

            <TextField
              label="WEBSITE"
              type="url"
              value={form.website}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
              placeholder="https://"
              autoCapitalize="none"
              testId="settings-website"
            />

            <ToggleRow
              Icon={Lock}
              title="Private account"
              subtitle="Only approved followers can see your posts."
              checked={form.account_privacy === "private"}
              onChange={(v) =>
                setForm((f) => ({ ...f, account_privacy: v ? "private" : "public" }))
              }
              testId="settings-privacy-toggle"
            />

            {err && <ErrorBox testId="edit-profile-error">{err}</ErrorBox>}

            <PrimaryButton
              label="Save changes"
              onClick={doSave}
              loading={saving}
              className="w-full mt-1"
              testId="settings-save"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
